import { useEffect, useState, useCallback, useMemo } from "react";
import Layout from "@/components/Layout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useClientScope } from "@/hooks/useClientScope";
import { toast } from "sonner";
import { ShieldQuestion, Loader2, EyeOff, Trash2, Flag, Search, Plus, X, Undo2 } from "lucide-react";
import { CategoryBadge, InsightChips, LangBadge, ConfidenceBar } from "@/components/CommentBits";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";

const RESULT_STYLE = {
  ok: "bg-green-50 text-green-700 border-green-200",
  failed: "bg-red-50 text-red-600 border-red-200",
  skipped: "bg-slate-100 text-slate-500 border-slate-200",
  pending: "bg-yellow-50 text-yellow-700 border-yellow-200",
};

export default function PassedComments() {
  const { user } = useAuth();
  const canAct = user?.role !== "client_viewer";
  const { ClientPicker, scopeParam, ready } = useClientScope();
  const [tab, setTab] = useState("passed");
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState("");
  const [search, setSearch] = useState("");
  const [items, setItems] = useState(null);
  const [mistakes, setMistakes] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [confirm, setConfirm] = useState(null); // { kind: "delete" | "flag", comment }
  const [busy, setBusy] = useState(false);

  const accountsById = useMemo(() => Object.fromEntries(accounts.map((a) => [a.id, a])), [accounts]);
  const flagAction = (c) => accountsById[c.account_id]?.rules?.action === "delete" ? "delete" : "hide";

  const query = useCallback((extra = "") => {
    const parts = [scopeParam, accountId && `account_id=${accountId}`, extra].filter(Boolean);
    return parts.length ? `?${parts.join("&")}` : "";
  }, [scopeParam, accountId]);

  const loadAccounts = useCallback(() => {
    if (!ready) return;
    api.get(`/accounts${scopeParam ? `?${scopeParam}` : ""}`).then(({ data }) => setAccounts(data)).catch(() => setAccounts([]));
  }, [scopeParam, ready]);

  const loadPassed = useCallback(() => {
    if (!ready) return;
    const s = search.trim() ? `search=${encodeURIComponent(search.trim())}` : "";
    api.get(`/comments${query(`status=passed${s ? `&${s}` : ""}`)}`).then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, [ready, query, search]);

  const loadMistakes = useCallback(() => {
    if (!ready) return;
    api.get(`/ai-mistakes${query()}`).then(({ data }) => setMistakes(data)).catch(() => setMistakes([]));
  }, [ready, query]);

  const loadSuggestions = useCallback(() => {
    if (!accountId) { setSuggestions([]); return; }
    api.get(`/accounts/${accountId}/rule-suggestions`).then(({ data }) => setSuggestions(data.suggestions)).catch(() => setSuggestions([]));
  }, [accountId]);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);
  useEffect(() => { setAccountId(""); }, [scopeParam]);
  useEffect(() => { const t = setTimeout(loadPassed, 250); return () => clearTimeout(t); }, [loadPassed]);
  useEffect(() => { loadMistakes(); }, [loadMistakes]);
  useEffect(() => { loadSuggestions(); }, [loadSuggestions]);

  const reload = () => { loadPassed(); loadMistakes(); loadSuggestions(); };

  const run = async (fn, okMessage) => {
    setBusy(true);
    try {
      await fn();
      toast.success(okMessage);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
      reload(); // a failed flag-and-remove still saves the flag, so always refresh
    }
  };

  const decide = (c, decision) =>
    run(() => api.post(`/comments/${c.id}/decision`, { decision }), decision === "delete" ? "Deleted" : "Hidden");

  const flag = (c) =>
    run(() => api.post(`/comments/${c.id}/flag-missed`),
      flagAction(c) === "delete" ? "Flagged and deleted" : "Flagged and hidden");

  const unflag = (id) => run(() => api.delete(`/comments/${id}/flag`), "Flag removed");

  const addKeyword = async (term) => {
    const acc = accountsById[accountId];
    const current = acc?.rules?.always_remove || [];
    await run(async () => {
      await api.put(`/accounts/${accountId}/rules`, { always_remove: [...current, term] });
      loadAccounts();
    }, `"${term}" added to always-remove`);
  };

  const dismiss = (term) =>
    run(() => api.post(`/accounts/${accountId}/rule-suggestions/dismiss`, { term }), "Suggestion dismissed");

  const confirmed = () => {
    const { kind, comment } = confirm;
    setConfirm(null);
    kind === "flag" ? flag(comment) : decide(comment, "delete");
  };

  const askDelete = (c) => setConfirm({ kind: "delete", comment: c });
  const askFlag = (c) => (flagAction(c) === "delete" ? setConfirm({ kind: "flag", comment: c }) : flag(c));

  return (
    <Layout>
      <div className="animate-fade-in-up space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-sm text-slate-400 font-medium">Comments the filter let through</p>
            <h1 className="font-heading text-3xl sm:text-4xl font-medium text-slate-900 tracking-tight">Passed Comments</h1>
          </div>
          <ClientPicker />
        </div>

        <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <div className="inline-flex rounded-lg bg-slate-100 p-1" role="tablist">
            {[["passed", "Passed"], ["log", `Mistake log${mistakes ? ` (${mistakes.length})` : ""}`]].map(([id, label]) => (
              <button key={id} role="tab" aria-selected={tab === id} onClick={() => setTab(id)} data-testid={`tab-${id}`}
                className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors ${tab === id ? "bg-card text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                {label}
              </button>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <select value={accountId} onChange={(e) => setAccountId(e.target.value)} data-testid="account-filter"
              className="px-3 py-2 rounded-lg border border-slate-200 bg-card text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-brass-500/40">
              <option value="">All accounts</option>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            {tab === "passed" && (
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search comment or author..." data-testid="passed-search"
                  className="pl-9 pr-4 py-2 rounded-md border border-slate-200 bg-card text-sm w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-brass-500/40" />
              </div>
            )}
          </div>
        </div>

        {tab === "passed" && (
          <>
            <SuggestionsPanel accountId={accountId} suggestions={suggestions} canAct={canAct} busy={busy}
              onAdd={addKeyword} onDismiss={dismiss} />
            {items === null ? (
              <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-indigo-600 animate-spin" /></div>
            ) : items.length === 0 ? (
              <Empty text="No passed comments match. Comments the filter keeps will appear here." />
            ) : (
              <div className="space-y-3">
                {items.map((c) => (
                  <div key={c.id} data-testid="passed-item" className="bg-card rounded-2xl border border-slate-100 shadow-card p-4">
                    <div className="flex items-center gap-2 flex-wrap mb-1.5">
                      <span className="font-semibold text-slate-800 text-sm">{c.author_name}</span>
                      <LangBadge language={c.language} />
                      <CategoryBadge category={c.ai?.category} />
                      <InsightChips insight={c.insight} />
                      {c.flagged && <span data-testid="flagged-badge" className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-md border bg-amber-50 text-amber-700 border-amber-200">Flagged · removal failed</span>}
                      <span className="text-xs text-slate-400">· {c.account_name} · {c.platform}</span>
                    </div>
                    <p className="text-sm text-slate-700 mb-2">{c.text}</p>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-3">
                        <ConfidenceBar value={c.ai?.confidence} />
                        <span className="text-xs text-slate-400 italic max-w-md truncate">{c.ai?.reasoning}</span>
                      </div>
                      {canAct && (
                        <div className="flex items-center gap-1.5">
                          <button disabled={busy} onClick={() => askFlag(c)} data-testid={`flag-${c.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-amber-50 text-amber-700 text-xs font-semibold hover:bg-amber-100 disabled:opacity-40"><Flag className="w-3.5 h-3.5" /> {c.flagged ? "Retry" : "Flag"} &amp; {flagAction(c)}</button>
                          <button disabled={busy} onClick={() => decide(c, "hide")} data-testid={`hide-${c.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-red-50 text-red-600 text-xs font-semibold hover:bg-red-100 disabled:opacity-40"><EyeOff className="w-3.5 h-3.5" /> Hide</button>
                          <button disabled={busy} onClick={() => askDelete(c)} data-testid={`delete-${c.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-slate-800 text-slate-50 text-xs font-semibold hover:bg-slate-900 disabled:opacity-40"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {tab === "log" && (
          mistakes === null ? (
            <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-indigo-600 animate-spin" /></div>
          ) : mistakes.length === 0 ? (
            <Empty text="No mistakes recorded. Flag a passed comment and it is logged here with what the filter said." />
          ) : (
            <div className="space-y-3">
              {mistakes.map((m) => (
                <div key={m.id} data-testid="mistake-item" className="bg-card rounded-2xl border border-slate-100 shadow-card p-4">
                  <div className="flex items-center gap-2 flex-wrap mb-1.5">
                    <LangBadge language={m.language} />
                    <CategoryBadge category={m.ai?.category} />
                    <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-md border ${RESULT_STYLE[m.action_result] || RESULT_STYLE.pending}`}>
                      {m.action_requested} {m.action_result}
                    </span>
                    <span className="text-xs text-slate-400">· {m.account_name} · flagged by {m.flagged_by} · {new Date(m.flagged_at).toLocaleString()}</span>
                  </div>
                  <p className="text-sm text-slate-700 mb-2">{m.text}</p>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-3 min-w-0">
                      <ConfidenceBar value={m.ai?.confidence} />
                      <span className="text-xs text-slate-400 italic truncate">{m.ai?.analyzer} · {m.ai?.reasoning}</span>
                    </div>
                    {canAct && (
                      <button disabled={busy} onClick={() => unflag(m.comment_id)} data-testid={`unflag-${m.comment_id}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-slate-100 hover:bg-indigo-50 hover:text-indigo-600 text-slate-600 text-xs font-semibold disabled:opacity-40"><Undo2 className="w-3.5 h-3.5" /> Remove flag</button>
                    )}
                  </div>
                  {m.action_error && <p className="text-xs text-red-500 mt-2">{m.action_error}</p>}
                </div>
              ))}
            </div>
          )
        )}
      </div>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="confirm-delete">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this comment?</AlertDialogTitle>
            <AlertDialogDescription>
              Deleting is permanent on {confirm?.comment.platform}. Restore only brings back hidden comments, not deleted ones.
              {confirm?.comment && <span className="block mt-2 text-slate-700">"{confirm.comment.text}"</span>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmed} data-testid="confirm-delete-yes">{confirm?.kind === "flag" ? "Flag & delete" : "Delete"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}

function Empty({ text }) {
  return (
    <div className="bg-card rounded-2xl border border-slate-100 p-12 text-center">
      <ShieldQuestion className="w-10 h-10 text-slate-300 mx-auto mb-3" />
      <p className="text-sm text-slate-500">{text}</p>
    </div>
  );
}

function SuggestionsPanel({ accountId, suggestions, canAct, busy, onAdd, onDismiss }) {
  if (!accountId) {
    return <p className="text-xs text-slate-400" data-testid="suggestions-hint">Pick an account to see always-remove keyword suggestions drawn from comments you flag.</p>;
  }
  if (!suggestions.length) return null;
  return (
    <div data-testid="suggestions-panel" className="bg-indigo-50/50 border border-indigo-100 rounded-2xl p-4">
      <p className="text-sm font-semibold text-slate-800">Suggested always-remove keywords</p>
      <p className="text-xs text-slate-500 mb-3">From comments you flagged. "Also in N kept" counts kept comments containing the same term, so a high number means adding it would hide legitimate comments.</p>
      <div className="flex flex-wrap gap-2">
        {suggestions.map((s) => (
          <div key={s.term} data-testid="suggestion" className="inline-flex items-center gap-2 bg-card border border-slate-200 rounded-lg pl-3 pr-1.5 py-1">
            <span className="text-sm font-medium text-slate-800">{s.term}</span>
            <span className="text-[11px] text-slate-400">{s.flagged_count} flagged</span>
            {s.passed_count > 0 && <span className="text-[11px] text-amber-600 font-semibold">also in {s.passed_count} kept</span>}
            {canAct && (
              <>
                <button disabled={busy} onClick={() => onAdd(s.term)} data-testid={`add-${s.term}`} title="Add to always-remove" className="p-1 rounded-md bg-indigo-600 text-indigo-50 hover:bg-indigo-700 disabled:opacity-40"><Plus className="w-3 h-3" /></button>
                <button disabled={busy} onClick={() => onDismiss(s.term)} data-testid={`dismiss-${s.term}`} title="Dismiss" className="p-1 rounded-md bg-slate-100 text-slate-500 hover:bg-slate-200 disabled:opacity-40"><X className="w-3 h-3" /></button>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
